// The states in which the API is still preparing a project (API 3.2). Anything else is settled (ready, archived, rejected, error).
export const IN_FLIGHT = new Set(['new', 'waiting_to_be_sent_to_tuner', 'waiting_for_identification', 'waiting_for_map_data']);

export const isInFlight = (state) => IN_FLIGHT.has(state);

/** The status poll's tick (R51: 5 s). The default fetch timeout is one tick, and `getStatus` cancels its own request at the same limit. */
export const STATUS_INTERVAL_MS = 5000;
export const STATUS_FETCH_TIMEOUT_MS = STATUS_INTERVAL_MS;

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

const TIMED_OUT = Symbol('timed out');

/** Resolves with the fetch's answer, or with TIMED_OUT when it has not answered after `ms`. A fetch that rejects rejects. */
const withTimeout = async (promise, ms) => {
    let timer;
    const timeout = new Promise(resolve => { timer = setTimeout(() => resolve(TIMED_OUT), ms); });
    try {
        return await Promise.race([promise, timeout]);
    } finally {
        clearTimeout(timer);
    }
};

/**
 * Polls one project's status: a fetch at once, then one every `intervalMs`. `onStatus` gets every answer; when the state
 * leaves IN_FLIGHT, `onTerminal` gets it and the poll ends. When `maxMs` of wall-clock time (by `now`) have passed with the project
 * still in flight it calls `onTakingLonger` and ends (R51: 5 s, "taking longer" after 3 minutes). A fetch that has not answered after
 * `fetchTimeoutMs` counts as failed, so a request that never answers cannot stall the clock. A failed fetch is not reported.
 * `stop()` ends the poll: an answer that arrives afterwards is dropped.
 * @returns {{stop: function()}}
 */
export const pollProjectStatus = ({projectId, fetchStatus, onStatus, onTerminal, onTakingLonger, intervalMs = STATUS_INTERVAL_MS, maxMs = 180000, fetchTimeoutMs = intervalMs, sleep = wait, now = Date.now}) => {
    let stopped = false;

    const run = async () => {
        const startedAt = now();
        while (!stopped) {
            let status = null;
            try {
                const answer = await withTimeout((async () => fetchStatus())(), fetchTimeoutMs);
                status = answer === TIMED_OUT ? null : answer;
            } catch (error) {
                // A failed fetch is a tick like any other; the next one tries again.
            }
            if (stopped) return;
            if (status) {
                onStatus(status);
                if (!isInFlight(status.state)) {
                    onTerminal(status);
                    return;
                }
            }
            await sleep(intervalMs);
            if (stopped) return;
            if (now() - startedAt >= maxMs) {
                onTakingLonger();
                return;
            }
        }
    };
    run();

    return {
        projectId,
        stop: () => { stopped = true; },
    };
};
